import { useState, useCallback, useEffect, useRef } from 'react';
import { ImagePosition, DragState } from '../types';

interface UseImageInteractionOptions {
  initialZooms?: Record<number, number>;
  initialPositions?: Record<number, ImagePosition>;
  isLinked?: boolean;
  viewMode?: string;
}

export const useImageInteraction = (options: UseImageInteractionOptions = {}) => {
  const [imagePositions, setImagePositions] = useState<Record<number, ImagePosition>>(options.initialPositions || {});
  const [imageZooms, setImageZooms] = useState<Record<number, number>>(options.initialZooms || {});
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    imageIndex: null,
    startX: 0,
    startY: 0
  });
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const containerRef = useRef<HTMLElement | null>(null);

  const zoomOptions = [50, 75, 100, 125, 150, 175, 200, 250, 300];

  const handleMouseDown = useCallback((e: React.MouseEvent, imageIndex: number) => {
    e.preventDefault();
    containerRef.current = e.currentTarget as HTMLElement;
    setDragState({
      isDragging: true,
      imageIndex,
      startX: e.clientX,
      startY: e.clientY
    });
  }, []);

  // Document-level mouse move handler for dragging
  const handleDocumentMouseMove = useCallback((e: MouseEvent) => {
    if (!dragState.isDragging || dragState.imageIndex === null || !containerRef.current) return;

    const deltaX = e.clientX - dragState.startX;
    const deltaY = e.clientY - dragState.startY;

    // Get the image container to calculate bounds - handle both DOM structures
    const container = containerRef.current;

    // Try direct img child first (GridAssetPanel structure)
    let image = container.querySelector('img') as HTMLImageElement;
    let imageContainer = container;

    // If no direct img, try nested structure (legacy ImageViewport structure)
    if (!image) {
      const nestedContainer = container.querySelector('div') as HTMLElement;
      image = nestedContainer?.querySelector('img') as HTMLImageElement;
      imageContainer = nestedContainer || container;
    }

    if (image && imageContainer) {
      const containerRect = imageContainer.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      const zoom = imageZooms[dragState.imageIndex!] || 1;

      // Check if mouse is still within the container bounds
      if (e.clientX < containerRect.left || e.clientX > containerRect.right ||
          e.clientY < containerRect.top || e.clientY > containerRect.bottom) {
        // Mouse left the container, stop dragging
        setDragState({
          isDragging: false,
          imageIndex: null,
          startX: 0,
          startY: 0
        });
        return;
      }

      // Calculate maximum allowed translation (50% of zoomed image can be outside)
      const maxTranslateX = (imageRect.width * zoom) * 0.5;
      const maxTranslateY = (imageRect.height * zoom) * 0.5;

      // Calculate new position
      const currentPos = imagePositions[dragState.imageIndex!] || { x: 0, y: 0 };
      let newX = currentPos.x + deltaX;
      let newY = currentPos.y + deltaY;

      // Clamp the position to bounds
      newX = Math.max(-maxTranslateX, Math.min(maxTranslateX, newX));
      newY = Math.max(-maxTranslateY, Math.min(maxTranslateY, newY));

      const newPosition = { x: newX, y: newY };

      // Handle synchronized panning when linked
      if (options.isLinked && options.viewMode !== 'single') {
        const otherImageIndex = dragState.imageIndex === 0 ? 1 : 0;
        setImagePositions(prev => ({
          ...prev,
          [dragState.imageIndex!]: newPosition,
          [otherImageIndex]: newPosition  // Sync both images
        }));
      } else {
        setImagePositions(prev => ({
          ...prev,
          [dragState.imageIndex!]: newPosition
        }));
      }
    }

    setDragState(prev => ({
      ...prev,
      startX: e.clientX,
      startY: e.clientY
    }));
  }, [dragState, imageZooms, imagePositions, options.isLinked, options.viewMode]);

  // Document-level mouse up handler
  const handleDocumentMouseUp = useCallback(() => {
    setDragState({
      isDragging: false,
      imageIndex: null,
      startX: 0,
      startY: 0
    });
  }, []);

  // React event handler for mouse move (for non-dragging scenarios)
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // This is kept for compatibility but dragging is handled by document listeners
    if (!dragState.isDragging) return;
  }, [dragState]);

  const handleMouseUp = useCallback(() => {
    setDragState({
      isDragging: false,
      imageIndex: null,
      startX: 0,
      startY: 0
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Stop dragging when mouse leaves the viewport
    setDragState({
      isDragging: false,
      imageIndex: null,
      startX: 0,
      startY: 0
    });
  }, []);

  // Manage document event listeners for dragging
  useEffect(() => {
    if (dragState.isDragging) {
      document.addEventListener('mousemove', handleDocumentMouseMove);
      document.addEventListener('mouseup', handleDocumentMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleDocumentMouseMove);
        document.removeEventListener('mouseup', handleDocumentMouseUp);
      };
    }
  }, [dragState.isDragging, handleDocumentMouseMove, handleDocumentMouseUp]);

  const handleZoomIn = useCallback((imageIndex: number) => {
    const currentZoom = imageZooms[imageIndex] || 1;
    const currentIndex = zoomOptions.findIndex(zoom => zoom >= currentZoom * 100);
    const nextIndex = Math.min(currentIndex + 1, zoomOptions.length - 1);
    const newZoom = Math.min(zoomOptions[nextIndex] / 100, 3); // Max 300%

    if (options.isLinked && options.viewMode !== 'single') {
      const otherImageIndex = imageIndex === 0 ? 1 : 0;
      setImageZooms(prev => ({
        ...prev,
        [imageIndex]: newZoom,
        [otherImageIndex]: newZoom
      }));
    } else {
      setImageZooms(prev => ({
        ...prev,
        [imageIndex]: newZoom
      }));
    }
  }, [imageZooms, options.isLinked, options.viewMode]);

  const handleZoomOut = useCallback((imageIndex: number) => {
    const currentZoom = imageZooms[imageIndex] || 1;
    const currentIndex = zoomOptions.findIndex(zoom => zoom >= currentZoom * 100);
    const prevIndex = Math.max(currentIndex - 1, 0);
    const newZoom = Math.max(zoomOptions[prevIndex] / 100, 0.5); // Min 50%

    if (options.isLinked && options.viewMode !== 'single') {
      const otherImageIndex = imageIndex === 0 ? 1 : 0;
      setImageZooms(prev => ({
        ...prev,
        [imageIndex]: newZoom,
        [otherImageIndex]: newZoom
      }));
    } else {
      setImageZooms(prev => ({
        ...prev,
        [imageIndex]: newZoom
      }));
    }
  }, [imageZooms, options.isLinked, options.viewMode]);

  const handleSetZoom = useCallback((imageIndex: number, zoomPercent: number, skipPositionReset: boolean = false) => {
    const clampedZoom = Math.max(50, Math.min(300, zoomPercent)) / 100; // Clamp between 50% and 300%

    if (options.isLinked && options.viewMode !== 'single') {
      const otherImageIndex = imageIndex === 0 ? 1 : 0;
      setImageZooms(prev => ({
        ...prev,
        [imageIndex]: clampedZoom,
        [otherImageIndex]: clampedZoom
      }));

      // Reset positions for both images if zoom goes below 100%
      if (clampedZoom <= 1 && !skipPositionReset) {
        setImagePositions(prev => ({
          ...prev,
          [imageIndex]: { x: 0, y: 0 },
          [otherImageIndex]: { x: 0, y: 0 }
        }));
      }
    } else {
      setImageZooms(prev => ({
        ...prev,
        [imageIndex]: clampedZoom
      }));

      // Reset position if zoom goes below 100% to prevent issues (but only if not explicitly skipped)
      if (clampedZoom <= 1 && !skipPositionReset) {
        setImagePositions(prev => ({
          ...prev,
          [imageIndex]: { x: 0, y: 0 }
        }));
      }
    }

    setActiveDropdown(null);
  }, [options.isLinked, options.viewMode]);

  const handleWheelZoom = useCallback((e: React.WheelEvent, imageIndex: number) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.01 : 0.01;
    const currentZoom = imageZooms[imageIndex] || 1;
    const newZoom = Math.max(0.5, Math.min(3, currentZoom + delta)); // Clamp between 50% and 300%

    if (options.isLinked && options.viewMode !== 'single') {
      const otherImageIndex = imageIndex === 0 ? 1 : 0;
      setImageZooms(prev => ({
        ...prev,
        [imageIndex]: newZoom,
        [otherImageIndex]: newZoom
      }));

      // Auto-reset positions for both images when zooming out below 100%
      if (newZoom <= 1 && currentZoom > 1) {
        setImagePositions(prev => ({
          ...prev,
          [imageIndex]: { x: 0, y: 0 },
          [otherImageIndex]: { x: 0, y: 0 }
        }));
      }
    } else {
      setImageZooms(prev => ({
        ...prev,
        [imageIndex]: newZoom
      }));

      // Auto-reset position when zooming out below 100%
      if (newZoom <= 1 && currentZoom > 1) {
        setImagePositions(prev => ({
          ...prev,
          [imageIndex]: { x: 0, y: 0 }
        }));
      }
    }
  }, [imageZooms, options.isLinked, options.viewMode]);

  const handleSetPosition = useCallback((imageIndex: number, x: number, y: number) => {
    setImagePositions(prev => ({
      ...prev,
      [imageIndex]: { x, y }
    }));
  }, []);

  const handleResetZoom = useCallback((imageIndex: number) => {
    if (options.isLinked && options.viewMode !== 'single') {
      const otherImageIndex = imageIndex === 0 ? 1 : 0;
      setImageZooms(prev => ({
        ...prev,
        [imageIndex]: 1,
        [otherImageIndex]: 1
      }));
      setImagePositions(prev => ({
        ...prev,
        [imageIndex]: { x: 0, y: 0 },
        [otherImageIndex]: { x: 0, y: 0 }
      }));
    } else {
      setImageZooms(prev => ({
        ...prev,
        [imageIndex]: 1
      }));
      setImagePositions(prev => ({
        ...prev,
        [imageIndex]: { x: 0, y: 0 }
      }));
    }
  }, [options.isLinked, options.viewMode]);

  return {
    imagePositions,
    imageZooms,
    dragState,
    activeDropdown,
    zoomOptions,
    setActiveDropdown,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleZoomIn,
    handleZoomOut,
    handleSetZoom,
    handleWheelZoom,
    handleResetZoom,
    handleSetPosition
  };
};